import { z } from 'zod'
import { UIBlockSchema } from './blocks'

// ============================================================================
// SSE Event Types
// ============================================================================

export const SSEEventType = z.enum(['title', 'summary', 'block', 'error', 'final', 'progress', 'text_delta'])
export type SSEEventType = z.infer<typeof SSEEventType>

// ============================================================================
// Title Event
// ============================================================================

export const TitleEventSchema = z.object({
  title: z.string(),
})

export type TitleEvent = z.infer<typeof TitleEventSchema>

// ============================================================================
// Summary Event
// ============================================================================

export const SummaryEventSchema = z.object({
  summary: z.string(),
})

export type SummaryEvent = z.infer<typeof SummaryEventSchema>

// ============================================================================
// Block Event
// ============================================================================

export const BlockEventSchema = z.object({
  block: UIBlockSchema,
})

export type BlockEvent = z.infer<typeof BlockEventSchema>

// ============================================================================
// Error Event
// ============================================================================

export const ErrorEventSchema = z.object({
  code: z.string(),
  message: z.string(),
})

export type ErrorEvent = z.infer<typeof ErrorEventSchema>

// ============================================================================
// Final Event
// ============================================================================

export const FinalEventSchema = z.object({
  status: z.enum(['completed', 'failed', 'partial']),
  trace_id: z.string().optional(),
  duration_ms: z.number().optional(),
  error: z.string().optional(),
  confidence: z.number().optional(),
  confidence_level: z.enum(['high', 'medium', 'low']).optional(),
  partial_result: z.boolean().optional(),
  timed_out_agents: z.array(z.string()).optional(),
})

export type FinalEvent = z.infer<typeof FinalEventSchema>

// ============================================================================
// Progress Event (P1 #17 — Progressive SSE Streaming)
// ============================================================================

export const ProgressEventSchema = z.object({
  type: z.literal('progress'),
  message: z.string(),
  agent_id: z.string().nullable().optional(),
  completed: z.number().nullable().optional(),
  total: z.number().nullable().optional(),
})

export type ProgressEvent = z.infer<typeof ProgressEventSchema>

// ============================================================================
// Text Delta Event (P1 #17 — Token-by-Token Synthesis)
// ============================================================================

export const TextDeltaEventSchema = z.object({
  content: z.string(),
})

export type TextDeltaEvent = z.infer<typeof TextDeltaEventSchema>

// ============================================================================
// SSE Message Union
// ============================================================================

export interface SSEMessage {
  event: SSEEventType
  data: TitleEvent | SummaryEvent | BlockEvent | ErrorEvent | FinalEvent | ProgressEvent | TextDeltaEvent
  id?: string
  timestamp: number
}

// ============================================================================
// Connection Status
// ============================================================================

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

// ============================================================================
// Stream State
// ============================================================================

export interface StreamState {
  status: ConnectionStatus
  title: string | null
  summary: string | null
  error: string | null
  traceId: string | null
  durationMs: number | null
  isComplete: boolean
  confidence: number | null
  confidenceLevel: 'high' | 'medium' | 'low' | null
  partialResult: boolean
}

// ============================================================================
// Parser Functions
// ============================================================================

export function parseTitleEvent(data: unknown): TitleEvent | null {
  const result = TitleEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseSummaryEvent(data: unknown): SummaryEvent | null {
  const result = SummaryEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseBlockEvent(data: unknown): BlockEvent | null {
  const result = BlockEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseErrorEvent(data: unknown): ErrorEvent | null {
  const result = ErrorEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseFinalEvent(data: unknown): FinalEvent | null {
  const result = FinalEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseProgressEvent(data: unknown): ProgressEvent | null {
  const result = ProgressEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseTextDeltaEvent(data: unknown): TextDeltaEvent | null {
  const result = TextDeltaEventSchema.safeParse(data)
  return result.success ? result.data : null
}
